import type { Cybo } from "./types.js";
import { forgetRoster } from "../../cybo-dm-peer-id.js";

// In-place roster ops for the create/delete broadcasts (#644). Both mutate the
// array directly (push / splice) so a Svelte 5 `$state` roster proxy reacts
// surgically — only the changed slot re-triggers, no whole-array reassignment
// (same in-place principle as #641's applyCyboUpdate). Both are idempotent: the
// actor (who already refetched/filtered locally) and every other member converge
// without duplicates or errors when the broadcast also reaches the actor.

/** Append a newly-created cybo, skipping if its id is already present. */
export function appendCybo(list: Cybo[], cybo: Cybo): void {
  if (list.some((c) => c.id === cybo.id)) return;
  list.push(cybo);
}

/** Remove a deleted cybo by id; no-op when it isn't in the list. */
export function removeCybo(list: Cybo[], cyboId: string): void {
  const idx = list.findIndex((c) => c.id === cyboId);
  if (idx !== -1) list.splice(idx, 1);
}

/**
 * Drop every AGENT BINDING for a deleted cybo (`workspaceState.agents`).
 *
 * removeCybo above prunes the roster; this prunes the sessions that point at it.
 * Both halves are needed: the DM sidebar row, the /agent route and ProfilePanel
 * all resolve a cybo's identity, status dot and composer from a BINDING, so a
 * pruned roster on its own left the deleted agent rendering as a live one.
 *
 * In place (splice), same reason as removeCybo — a wholesale reassignment of the
 * $state roster re-renders every session row on every delete. Reverse iteration
 * so removing one index never skips the next. Idempotent: no matches, no-op.
 */
export function removeCyboSessions<T extends { cyboId?: string | null }>(
  agents: T[],
  cyboId: string,
): void {
  let removed = false;
  for (let i = agents.length - 1; i >= 0; i--) {
    if (agents[i]?.cyboId === cyboId) {
      agents.splice(i, 1);
      removed = true;
    }
  }
  // canonicalAgentDmPeerId keeps one id map per roster array; this edit is in place.
  if (removed) forgetRoster(agents);
}
